import React from "react";
import { Link } from "react-router-dom";
import { SITE } from "@/data/site";
import { useBranding } from "@/context/BrandingContext";
import { useSiteSettings } from "@/context/SiteContext";
import { mediaUrl } from "@/lib/api";
import { Twitter, Youtube, Instagram, Facebook, Linkedin, ShoppingBag, MessageCircle, GraduationCap, BookMarked } from "lucide-react";

const SOCIAL_META = [
  { key: "twitter", label: "X / Twitter", Icon: Twitter },
  { key: "youtube", label: "YouTube", Icon: Youtube },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "amazon", label: "Amazon Author Page", Icon: ShoppingBag },
  { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
  { key: "academia", label: "Academia.edu", Icon: GraduationCap },
  { key: "goodreads", label: "Goodreads", Icon: BookMarked },
];

export const Footer = () => {
  const { logo_url, alt_text } = useBranding();
  const settings = useSiteSettings();
  const publisherName = settings?.publisher_name || SITE.name;
  const mission = settings?.mission || SITE.mission;
  const footerTagline = settings?.footer_tagline || "Rediscovering India · Evidence · Exploration · Inquiry";
  const columns = settings?.footer_columns || [];
  const socials = settings?.social_links || {};
  const activeSocials = SOCIAL_META.filter(s => (socials[s.key] || "").trim());

  return (
    <footer
      className="mt-24 border-t paper-noise"
      style={{ background: "var(--gi-ivory-2)", borderColor: "var(--gi-border)" }}
      data-testid="site-footer"
    >
      <div className="container-wide py-16 grid gap-12 lg:grid-cols-4">
        <div>
          <Link to="/" className="inline-block" data-testid="footer-logo" aria-label={`${publisherName} — Home`}>
            {logo_url ? (
              <img
                src={mediaUrl(logo_url)}
                alt={alt_text}
                className="w-auto object-contain h-[48px] max-w-[160px] sm:h-[60px] sm:max-w-[220px]"
                data-testid="footer-logo-image"
              />
            ) : (
              <div className="font-heading text-2xl" style={{ color: "var(--gi-maroon)" }} data-testid="footer-publisher-name">{publisherName}</div>
            )}
          </Link>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }} data-testid="footer-mission">
            {mission}
          </p>
          {activeSocials.length > 0 && (
            <div className="mt-5 flex items-center gap-3" data-testid="footer-socials">
              {activeSocials.map(({ key, label, Icon }) => (
                <a
                  key={key}
                  href={socials[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className="w-9 h-9 border flex items-center justify-center hover:bg-[var(--gi-maroon)] hover:text-[var(--gi-ivory)] transition-colors"
                  style={{ borderColor: "var(--gi-border)", color: "var(--gi-maroon)" }}
                  data-testid={`footer-social-${key}`}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          )}
        </div>
        {columns.map((col, i) => (
          <FooterCol
            key={col.title || `col-${i}`}
            title={col.title}
            links={col.links || []}
            testPrefix={`footer-${(col.title || "col").toLowerCase().replace(/\s+/g, "-")}`}
          />
        ))}
      </div>
      <div className="hair-divider" />
      <div className="container-wide py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="text-xs" style={{ color: "var(--gi-muted)" }} data-testid="footer-copyright">
          © {new Date().getFullYear()} {publisherName}. All rights reserved.
        </div>
        <div className="font-accent text-[10px]" style={{ color: "var(--gi-muted)" }} data-testid="footer-tagline">
          {footerTagline}
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links, testPrefix }) => (
  <div>
    <div className="font-accent text-[10px] mb-4" style={{ color: "var(--gi-maroon)" }}>{title}</div>
    <ul className="space-y-2.5">
      {links.map(l => {
        const isExternal = /^https?:\/\//i.test(l.to || "");
        const key = `${l.to}-${l.label}`;
        const testId = `${testPrefix}-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
        return (
          <li key={key}>
            {isExternal ? (
              <a href={l.to} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: "var(--gi-charcoal)" }} data-testid={testId}>{l.label}</a>
            ) : (
              <Link to={l.to} className="text-sm hover:underline" style={{ color: "var(--gi-charcoal)" }} data-testid={testId}>{l.label}</Link>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

export default Footer;
